// ─────────────────────────────────────────────────────────────────────────────
// DashboardColumns — the Phase-6.7 Dashboard tab panels (desktop, render-only).
//
// Additive to the unchanged homepage: the Console tab is the landing page; the
// Dashboard tab swaps ONLY the left/right side columns while RexyHomepage keeps the
// centre orb mounted — so the PTT sphere stays centre and the voice session is never
// lost on tab toggle (AppShell owns the PTT lifecycle; nothing here touches it).
//
// SCOPE (6.7 ruling, render-only Option 1): READS already-built engine output. Adds
// NO new read authority, NO persistence, NO cognition, NO emission path. It displays.
//   • WIRED real source:
//       - Active agents → GET /api/dashboard/activity (Phase-3.5 observe_fleet_activity,
//                         Rexy's CEO self-report — NOT the Phase-8 per-worker FLEET board).
//   • No empty-state panels remain. (Pulse schedule is WIRED — Stage-2 Step 2,
//     GET /api/rexy/pulse-schedule via PulseSchedulePanel.)
//
// Owner ruling 2026-07-24 — UI-only removal, three panels off the Dashboard tab:
// Orchestration log, Per-domain pull status, Dictation events. Backend untouched by
// construction (no endpoint/module removal): /api/dashboard/pull-status and
// core/pull_scheduler.py remain live and are simply no longer read from this surface.
//
// Palette: REXY_CONSOLE only (cyan #00BFFF / dark), bubble-highlight card
// idiom carried over from TelemetryCard. PERSONAL/TRADING are always present, never
// filtered, in every panel.
// ActivityPanel (ACTIVITY Step-1, JOINT-signed v4) now mounts in place of ActiveAgentsPanel.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../../lib/rexyConsole';
import RexyCalendarPanel from './RexyCalendarPanel';
import ClockSettingsPanel from './ClockSettingsPanel';
import ActivityPanel from './ActivityPanel';
import NotesPanel from './NotesPanel';
import ReportsPanel from './ReportsPanel';
import DreamstatePanel from './DreamstatePanel';
import PulseSchedulePanel from './PulseSchedulePanel';

const MONO = REXY_FONT;

// ── shared panel shell — same bubble-highlight card chrome as TelemetryCard ──────
function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={styles.card}>
      <div style={styles.sectionHeader}>{title}</div>
      {children}
    </div>
  );
}

// ── PANEL 2 (WIRED): Active agents — CEO self-report (Phase-3.5) ─────────────────
function ActiveAgentsPanel() {
  const [view, setView] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const token = (window as any).__rexyToken;
    fetch('/api/dashboard/activity', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.json())
      .then((j) => (j && j.success ? setView(j.activity) : setErr((j && j.error) || 'unavailable')))
      .catch((e) => setErr(String(e)));
  }, []);

  return (
    <Panel title="║ ACTIVE AGENTS">
      {/* This is Rexy's CEO self-report view — what a domain agent filed / is working.
          NOT the post-Phase-8 per-worker FLEET board (a separate, later surface). */}
      <div style={styles.subtle}>CEO self-report · NOT the Phase-8 FLEET board</div>
      {err ? (
        <div style={styles.degraded}>not_available — {err}</div>
      ) : !view ? (
        <div style={styles.degraded}>loading…</div>
      ) : (
        <div>
          {/* P1c — `active` = genuinely-live workers ONLY (0 when none running);
              `claimed` = stale task-file claims, labelled unverified when liveness
              isn't determinable. Never conflate the two (the WIRE="active 21" bug). */}
          <div style={styles.fleetRow}>
            <span>active {view.fleet_state?.active ?? 0}</span>
            <span>
              claimed {view.fleet_state?.claimed ?? 0}
              {view.live_verified ? '' : ' (unverified)'}
            </span>
            <span>pending {view.fleet_state?.pending ?? 0}</span>
            <span>done {view.fleet_state?.done_recent ?? 0}</span>
          </div>

          {/* Genuinely-live units across ALL domains (universal fleet-emission). */}
          {(view.active_workers || []).length === 0 ? (
            <div style={styles.degraded}>
              {view.live_verified
                ? 'no live units'
                : 'no live units (runtime not readable — claims unverified)'}
            </div>
          ) : (
            <div style={styles.list}>
              {(view.active_workers || []).map((w: any, i: number) => (
                <div key={`w${i}`} style={styles.itemRow}>
                  <span style={styles.itemAgent}>{w.unit || w.worker_id || '—'}</span>
                  <span style={styles.itemSub}>
                    {w.domain || 'CODE'} · {w.fleet || '—'} · {w.state || 'live'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* RECENT — proposals/wraps are HISTORY, never the active block (this is
              where the WIRE·rejected proposal correctly lives). */}
          {(view.build_activity || []).length > 0 && (
            <>
              <div style={styles.recentHeader}>RECENT (proposals · wraps)</div>
              <div style={styles.list}>
                {(view.build_activity || []).map((a: any, i: number) => (
                  <div key={`a${i}`} style={styles.itemRow}>
                    <span style={styles.itemAgent}>{a.agent || '—'}</span>
                    <span style={styles.itemSub}>{a.domain} · {a.activity_type} · {a.state}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </Panel>
  );
}

// ── PANEL 4 (WIRED, Stage-2 Step 4): the calendar — NOTES/REPORTS by day ─────────
// The former empty-state's backing stores now exist (steps 2-3): the calendar IS
// the notes-&-reports surface — click a date, the day's NOTES (her hourly REXY_NOTE
// comprehension, Step 3 §5A — repointed off dreamstate) / REPORTS (role artifacts)
// open beside its timestamped activity (D5).

// ── PANEL 5 (WIRED, Stage-2 Step 2): Pulse schedule — her proof of life ───────────
// Lives in its own file (PulseSchedulePanel.tsx): GET /api/rexy/pulse-schedule,
// next fires + the accrued hourly slots green ✓ / red ✗, newest-first.

// ── Column composition — orb stays centre; these fill the two side asides ────────
export function DashboardLeft() {
  return (
    <>
      <ActivityPanel collapseKey="activity" />
      {/* NOTES sits directly under ACTIVITY (§5C — Notes first; Reports & Dreamstate
          follow in this step). Her comprehension in her own words (§5A). */}
      <NotesPanel collapseKey="notes" />
      {/* REPORTS — the SECOND box under ACTIVITY (Step 3 Piece 2): her 6-hour
          summary reasoned across her own NOTES, verbatim, honest states. */}
      <ReportsPanel />
      {/* DREAMSTATE — the THIRD box under ACTIVITY (Step 3 Piece 3, Part 1):
          her nightly Tier-1 SYNTHESIS mirrored verbatim, honest night states. */}
      <DreamstatePanel collapseKey="dreamstate" />
    </>
  );
}

export function DashboardRight() {
  return (
    <>
      <RexyCalendarPanel />
      <ClockSettingsPanel />
      <PulseSchedulePanel />
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    background: REXY_CONSOLE.cardBg,
    borderRadius: 6,
    padding: 14,
    // Dimensional, never flat (CLAUDE.md UI §3) — same bubble idiom as TelemetryCard.
    boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)',
    minWidth: 0,
    maxWidth: '100%',
    overflowWrap: 'anywhere',
  },
  sectionHeader: {
    fontWeight: 700,
    fontFamily: MONO,
    fontSize: 12,
    letterSpacing: 2,
    color: REXY_CONSOLE.primary,
    marginBottom: 10,
  },
  subtle: {
    fontFamily: MONO,
    fontSize: 9,
    letterSpacing: 1,
    color: REXY_CONSOLE.dimText,
    marginBottom: 8,
  },
  fleetRow: {
    display: 'flex',
    gap: 12,
    fontFamily: MONO,
    fontSize: 10,
    color: REXY_CONSOLE.infText,
    marginBottom: 8,
  },
  list: { display: 'flex', flexDirection: 'column', gap: 2 },
  recentHeader: {
    fontFamily: MONO,
    fontSize: 9,
    letterSpacing: 1.5,
    color: REXY_CONSOLE.dimText,
    marginTop: 10,
    marginBottom: 4,
  },
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 10,
    padding: '3px 4px',
    borderBottom: `1px solid ${REXY_CONSOLE.rowDivider}`,
  },
  itemAgent: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.primary },
  itemSub: {
    fontFamily: MONO,
    fontSize: 9,
    color: REXY_CONSOLE.dimText,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  degraded: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.dimText, padding: '4px 0' },
};
